<template>
  <div class="document" :style="`background-image: url('${src}');`">

    <div v-if="pageCount > 1" class="controls">
      <font-awesome-icon :icon="['far', 'arrow-alt-circle-left']" @mousedown.stop size="2x" @click="prevPage"/>
      <font-awesome-icon :icon="['far', 'arrow-alt-circle-right']" @mousedown.stop size="2x" @click="nextPage"/>
    </div>
  </div>
</template>


<script>
import range from 'lodash/range';
import pdfjs from 'pdfjs-dist';

function getDocument(url) {
  return pdfjs.getDocument(url);
}

function getPages(pdf, first, last) {
  const allPages = range(first, last+1).map(number => pdf.getPage(number));
  return Promise.all(allPages);
}


function getDefaults() {
  return {
    pages: [],
    cursor: 0,
  };
}

export default {
  name: 'PreviewPDF',

  props: {
    url: '',
  },

  data() {
    return Object.assign(getDefaults(), {
      pdf: undefined,
      src: undefined,
      page: {}
    });
  },

  watch: {
    url: {
      handler(url) {
        if (url !== null) {
        getDocument(url)
          .then(pdf => (this.pdf = pdf))
          .catch(response => {
            this.$emit('document-errored', {text: 'Failed to retrieve PDF', response});
          });
        }
      },
      immediate: true,
    },

    pdf(pdf, oldPdf) {
      if (!pdf) return;
      if (oldPdf) Object.assign(this, getDefaults());
      this.$bus.$emit('filemanager::pages::count', this.pageCount);
      this.fetchPages();
    },
  },

  computed: {
    current() {
      return this.page.pageNumber - 1;
    },
    pageCount() {
      return this.pdf ? this.pdf.numPages : 0;
    },
    viewport() {
      return this.page.getViewport(1.0);
    },
  },

  methods: {
    fetchPages(currentPage = 0) {
      if (!this.pdf) return;
      if (this.pageCount > 0 && this.pages.length === this.pageCount) return;

      const startIndex = this.pages.length;
      if (this.cursor > startIndex) return;

      const startPage = startIndex + 1;
      const endPage = Math.min(Math.max(currentPage, startIndex + this.pageCount), this.pageCount);
      this.cursor = endPage;

      getPages(this.pdf, startPage, endPage)
        .then((pages) => {

          let pagesMapped = pages.map(page => ({
            page,
            id: 0,
            page_num: page.pageNumber
          }));

          const deleteCount = 0;
          this.pages.splice(startIndex, deleteCount, ...pagesMapped);
          return this.pagesMapped;
        }).then(() => {
          this.page = this.pages[0].page;
          this.drawPage();
        })
        .catch((response) => {
          this.$emit('document-errored', {text: 'Failed to retrieve pages', response});
        });
    },

    prevPage() {
      if (this.current == 0) return;
      this.page = this.pages[this.current - 1].page;
      this.renderTask = false;
      this.drawPage();
    },

    nextPage() {
      if (this.current == (this.pageCount - 1)) return;
      this.page = this.pages[this.current + 1].page;
      this.renderTask = false;
      this.drawPage();
    },

    onPageRendered({text, page}) {
    },

    onPageErrored({text, response, page}) {
    },

    drawPage() {
      if (this.renderTask) return;

      const {viewport} = this;
      const canvas = document.createElement('canvas')
      const canvasContext = canvas.getContext('2d')
      const renderContext = { canvasContext, viewport };
      canvas.height = viewport.height;
      canvas.width = viewport.width;

      this.renderTask = this.page.render(renderContext);
      this.renderTask
        .then(() => {
          this.src = canvas.toDataURL();
          canvas.width = 0;
          canvas.height = 0;
        }).then(() => {
          return this.page.getTextContent();
        }).then((textContent) => {
          textContent.items.forEach(span => {
              const regex = new RegExp('signature', 'i');
              let match = regex.test(span.str);
              if (!match) return
              this.signatureSuggestedPage.push(this.pageNumber);
          });
        }).then(() => {
          this.$bus.$emit('filemanager::thumbnail::rendered', {
            page: this.page,
            text: `Rendered thumbnail ${this.pageNumber}`,
          });
         })
        .catch(response => {
          this.destroyRenderTask();
          this.$emit('thumbnail-errored', {
            response,
            page: this.page,
            text: `Failed to render thumbnail ${this.pageNumber}`,
          });
        });
    },

    destroyPage(_newPage, page) {
      if (page) page._destroy();
      this.destroyRenderTask();
    },

    destroyRenderTask() {
      if (!this.renderTask) return;
      this.renderTask.cancel();
      delete this.renderTask;
    },
  },
  beforeDestroy() {
    this.$bus.$off('pdf::page::rendered');
    this.$bus.$off("pdf::page::errored");
    this.$bus.$off('pdf::pages::fetch');
  },
  mounted() {
    this.$bus.$on('pdf::page::rendered', this.onPageRendered);
    this.$bus.$on('pdf::page::errored', this.onPageErrored);
    this.$bus.$on('pdf::pages::fetch', this.fetchPages);
  },
};
</script>
<style lang="scss" scoped>
  .document {
    display: flex;
    align-items: flex-end;
    justify-content: center;

    .controls {
      font-size: 12px;

      .svg-inline--fa {
        opacity: 0.5;
        cursor: pointer;

        &:hover {
          opacity: 1;
        }
      }
    }
  }
</style>
